<template>
  <div
    v-if="routeChunkError"
    class="fixed inset-0 z-[9999] flex h-screen w-full items-center justify-center bg-surf-bg-def"
  >
    <progress-error :error-message="routeChunkError" />
  </div>
  <component
    :is="layout"
    v-else-if="layout"
  >
    <router-view
      :active-aside="activeAside"
    />
  </component>
  <wui-dialog-wrapper />
  <wui-notify-wrapper />
</template>

<script lang="ts" setup>
import type { EventCallback } from '@tauri-apps/api/event'
import { useDialog, WuiDialogWrapper, authPluginKey, safeInject, WuiNotifyWrapper, AuthApi } from '@wui/common-library'
import { DefineComponent, shallowRef, watchEffect, ref, watch, computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'

import { routeChunkError } from '@/app/router'
import { useStoreSettingsNotifications } from '@/app/store/pinia/settings/notifications'
import { useLiveCallSessions } from '@/components/LayoutApplication/useLiveCallSessions'

import type { ActiveAsideType } from '@/features/call-page-aside'
import { useTimezoneSelector } from '@/features/timezone-selector'

import { ProgressError } from '@/shared/components/loaders'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useTauriEventListener } from '@/shared/composables/tauri'
import { useForceReloadSignal } from '@/shared/composables/use-force-reload-signal'
import { useNotification } from '@/shared/composables/use-notification'
import { useWebRTC } from '@/shared/services/jssip'
import { CustomTauriEvent } from '@/shared/types/tauri'
import { isPWAWindow } from '@/shared/utils'
import { tauriMode } from '@/shared/utils/import-meta'
import { handlerSystemTray } from '@/shared/utils/tauri'

import { usePreferencesStore } from './entities/preference'

const ReconnectDialog = defineAsyncComponent(() => import('@/shared/components/dialogs/reconnect-dialog/reconnect-dialog.vue'))

const { t } = useLocalization()
const settingsNotificationsStore = useStoreSettingsNotifications()

usePreferencesStore()
useForceReloadSignal()

const { showDialog } = useDialog()
const { resetCallStatusIfChangedByCall } = settingsNotificationsStore
const { currentUser, initCurrentUser } = useCurrentUser()
initCurrentUser()


const { token } = safeInject<AuthApi>(authPluginKey)

const layout = shallowRef<DefineComponent>()
const route = useRoute()
const activeAside = ref<ActiveAsideType>(null) // Для GuestCallSession

if (tauriMode()) {
  const tauriEvent = useTauriEventListener()
  tauriEvent.listenEvent(CustomTauriEvent.SYSTEM_TRAY_EVENT, handlerSystemTray as EventCallback<unknown>)
}

if (token.value && currentUser.value) {
  // В роутере на данный момент path= "/"
  if (window.location.pathname.includes('/guest-')) {
    localStorage.clear()
  }
}

useTimezoneSelector().refreshTzSettings()

watchEffect(() => {
  if (route.meta?.layout) {
    layout.value = route.meta.layout as DefineComponent
  }
})

const onBeforeUnload = (event: Event): void => {
  beforeCloseApp()
  if (isPWAWindow()) {
    event.preventDefault()
    event.returnValue = false
    showDialog(ReconnectDialog)
  } else {
    useNotification().closeAllNotifications()
  }
}

const beforeCloseApp = () => {
  useLiveCallSessions().liveCallSessions.value.forEach(session => session.terminate())
  useWebRTC().stop()

  resetCallStatusIfChangedByCall()
}

window.addEventListener('beforeunload', onBeforeUnload, { capture: true })

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    useNotification().closeAllNotifications()
  }
})

const metaTitle = computed(() => {
  const hasTitle = !!(route.meta.title as string)?.length
  const text: string = hasTitle ? t(route.meta.title as string) : '📱'
  if (isPWAWindow()) {
    return text
  }
  const clientName = tauriMode() ? t('about.rtuDesktopClient') : t('about.rtuClient')
  return `${text} - ${clientName} (${t('terminals.softPhone')})`
})

watch(() => metaTitle.value, (title) => {
  document.title = title
})
</script>
